import React, { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { useSelector, useDispatch } from "react-redux";
import { getUserLocation, setUserLocation } from "@/redux/slices/UserLocation";

export const CACHE_KEY = "userCountry";
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24h -- travellers and VPN users move.

// Returns the cached country, or null if it is absent, expired or malformed.
// Entries written before this key held a JSON payload are dropped on read:
// they may carry a country that was defaulted rather than actually detected.
export const readCachedCountry = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;

    const { country, timestamp } = JSON.parse(raw);
    if (!country || !timestamp || Date.now() - timestamp > CACHE_TTL) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }

    return country;
  } catch {
    localStorage.removeItem(CACHE_KEY);
    return null;
  }
};

export const writeCachedCountry = (country) =>
  localStorage.setItem(
    CACHE_KEY,
    JSON.stringify({ country, timestamp: Date.now() })
  );

// Reads the stored payload without applying the TTL. Consumers that only need
// to display the current selection should not trigger a re-detection.
export const peekCachedCountry = () => {
  try {
    return JSON.parse(localStorage.getItem(CACHE_KEY))?.country ?? null;
  } catch {
    return null;
  }
};

const LocationWrapper = ({ children }) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { country, loading, error } = useSelector(
    (state) => state.userLocation
  );
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkUserLocation = async () => {
      const cachedLocation = readCachedCountry();
      if (cachedLocation) {
        dispatch(
          setUserLocation({
            country: cachedLocation,
            cached: true,
            timestamp: Date.now(),
          })
        );
        handleLocationRedirect(cachedLocation);
        return;
      }

      if (!country && !loading) {
        const result = await dispatch(getUserLocation());

        if (result.payload) {
          const userCountry = result.payload.country;

          writeCachedCountry(userCountry);

          handleLocationRedirect(userCountry);
        } else {
          // Detection failed. Leave the visitor on the page they asked for and
          // cache nothing -- caching a guess here would pin them to the wrong
          // site until they cleared site data.
          setIsChecking(false);
        }
      } else if (country) {
        handleLocationRedirect(country);
      }
    };

    const handleLocationRedirect = (userCountry) => {
      const currentPath = router.asPath;
      // Split path and query string
      const [pathname, queryString] = currentPath.split("?");
      if (userCountry !== "US" && !pathname.endsWith("/intl")) {
        let redirectPath = `${pathname}/intl`;
        if (queryString) {
          redirectPath += `?${queryString}`;
        }
        router.push(redirectPath);
        return;
      }

      if (userCountry === "US" && pathname.endsWith("/intl")) {
        let redirectPath = pathname.replace("/intl", "");
        if (queryString) {
          redirectPath += `?${queryString}`;
        }
        // console.log("CheckingLocation:RedirectPath:", redirectPath);
        router.push(redirectPath === "" ? "/" : redirectPath);
        return;
      }
      setIsChecking(false);
    };

    checkUserLocation();
  }, [country, loading, dispatch, router]);

  // if (isChecking || loading) {
  //   return (
  //     <div className="flex items-center justify-center min-h-screen">
  //       <div className="text-center">
  //         <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
  //         <p className="mt-4 text-gray-600">Detecting your location...</p>
  //       </div>
  //     </div>
  //   );
  // }

  // if (error) {
  // }

  return (
    <>
      {children}
      {(isChecking || loading) && (
        <div className="fixed inset-0 bg-white bg-opacity-90 flex items-center justify-center z-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
            <p className="mt-4 text-gray-600"></p>
          </div>
        </div>
      )}
    </>
  );
};

export default LocationWrapper;
